// ABAC Types for Frontend

export type ScopeType = 'GLOBAL' | 'TENANT' | 'DEPARTMENT' | 'TEAM' | 'SELF';

export interface Role {
  id: string;
  tenantId: string;
  name: string;
  displayName: string;
  description?: string;
  baseRole?: string;
  parentRoleId?: string;
  permissions: string[];
  deniedPermissions: string[];
  maxAssignees?: number;
  requiresMfa: boolean;
  allowedDepartments: string[];
  allowedLocations: string[];
  isActive: boolean;
  isSystem: boolean;
  assigneeCount: number;
  createdAt: string;
  updatedAt: string;
}

export type RoleAssignmentMode = 'DIRECT' | 'GOVERNED' | 'UNAVAILABLE';

export interface RoleAssignmentOption {
  role: Role;
  assignmentMode: RoleAssignmentMode;
  assignmentReason: string;
  riskLevel: AbacGovernanceRiskLevel;
  riskReasons: string[];
  allowedScopes: ScopeType[];
}

export interface SystemRoleDefinition {
  name: string;
  displayName: string;
  description: string;
  baseRole: string | null;
  permissionCount: number;
  isSystem: boolean;
}

export interface CreateRoleDto {
  name: string;
  displayName: string;
  description?: string;
  baseRole?: string;
  parentRoleId?: string;
  permissions: string[];
  deniedPermissions?: string[];
  maxAssignees?: number;
  requiresMfa?: boolean;
  allowedDepartments?: string[];
  allowedLocations?: string[];
}

export interface UpdateRoleDto {
  displayName?: string;
  description?: string;
  permissions?: string[];
  deniedPermissions?: string[];
  maxAssignees?: number;
  requiresMfa?: boolean;
  allowedDepartments?: string[];
  allowedLocations?: string[];
  isActive?: boolean;
}

export interface RoleAssignment {
  id: string;
  userId: string;
  roleId: string;
  roleName: string;
  roleDisplayName: string;
  scopeType: ScopeType;
  scopeValue?: string;
  source: string;
  assignedBy?: string;
  expiresAt?: string;
  createdAt: string;
}

export interface AssignRoleDto {
  roleId: string;
  scopeType: ScopeType;
  scopeValue?: string;
  expiresAt?: string;
}

export interface BulkAssignRolesDto {
  userIds: string[];
  roleId: string;
  scopeType: ScopeType;
  scopeValue?: string;
}

export interface UserRoles {
  userId: string;
  assignments: RoleAssignment[];
  effectivePermissions: string[];
  roleNames: string[];
}

/**
 * Detailed permission information with descriptions
 */
export interface PermissionDetail {
  /** Permission string e.g., 'crm:lead:read:tenant' */
  permission: string;
  /** Human-readable name e.g., 'View All Leads' */
  name: string;
  /** Description of what this permission allows */
  description: string;
  /** Action type e.g., 'read', 'create', 'update', 'delete' */
  action: string;
  /** Scope of the permission e.g., 'tenant', 'self', 'team' */
  scope: string;
  /** Category for grouping e.g., 'CRM - Leads' */
  category: string;
}

export interface PermissionModule {
  /** Module identifier e.g., 'crm' */
  module: string;
  /** Human-readable module name e.g., 'CRM' */
  displayName: string;
  /** Permissions in this module with full details */
  permissions: PermissionDetail[];
}

export interface RolePermissionMatrix {
  roleId: string;
  roleName: string;
  displayName: string;
  permissions: Record<string, boolean>;
}

export interface BulkAssignResult {
  assigned: number;
  skipped: number;
}

export type AbacGovernanceAction =
  | 'ROLE_CREATE'
  | 'ROLE_UPDATE'
  | 'ROLE_DELETE'
  | 'ROLE_ASSIGN'
  | 'ROLE_REVOKE'
  | 'ROLE_BULK_ASSIGN';

export type AbacGovernanceStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'FAILED';

export type AbacGovernanceRiskLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface GovernanceRequest {
  id: string;
  tenantId: string;
  action: AbacGovernanceAction;
  status: AbacGovernanceStatus;
  riskLevel: AbacGovernanceRiskLevel;
  targetRoleId?: string;
  targetRoleName?: string;
  targetUserId?: string;
  targetUserDisplayName?: string;
  requestedBy: string;
  requestedByDisplayName?: string;
  requestedAt: string;
  reason?: string;
  reviewedBy?: string;
  reviewedByDisplayName?: string;
  reviewedAt?: string;
  reviewComment?: string;
  appliedAt?: string;
  failureMessage?: string;
  payload: Record<string, unknown>;
  oldValue?: Record<string, unknown>;
  newValue?: Record<string, unknown>;
  changedFields: string[];
  riskReasons: string[];
  createdAt: string;
  updatedAt: string;
  canCurrentUserApprove?: boolean;
}

export interface CreateGovernanceRequestDto {
  action: AbacGovernanceAction;
  payload: Record<string, unknown>;
  reason?: string;
}

export interface ReviewGovernanceRequestDto {
  comment?: string;
}

// Backend-supported base roles that can currently be inherited by tenant custom roles.
// Keep this aligned with getSystemRoleDefinitionByBaseRole() in @unitree/abac.
export const SYSTEM_ROLE_OPTIONS = [
  {
    value: 'TENANT_ADMIN',
    label: 'Tenant Administrator',
    description: 'Tenant-wide setup, users, security, roles, and organization settings.',
  },
  {
    value: 'DEPARTMENT_HEAD',
    label: 'Department Head',
    description: 'Department-level employees, approvals, attendance, and reporting.',
  },
  {
    value: 'PEOPLE_MANAGER',
    label: 'People Manager',
    description: 'Direct-report approvals and people follow-up through the reporting chain.',
  },
  {
    value: 'TEAM_LEAD',
    label: 'Team Lead',
    description: 'Day-to-day team coordination and operational approvals.',
  },
  {
    value: 'EMPLOYEE',
    label: 'Employee',
    description: 'Default employee self-service access.',
  },
] as const;

export const SYSTEM_ROLES = SYSTEM_ROLE_OPTIONS.map((role) => role.value);

export type SystemRole = (typeof SYSTEM_ROLE_OPTIONS)[number]['value'];
